import React, { useState } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import { createService } from '../../services/api';

const AddServiceModal = ({ isOpen, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        shortCode: '',
        description: '',
        category: '',
        isMultiSession: false
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Service name is required';
        } else if (formData.name.trim().length < 3) {
            newErrors.name = 'Service name must be at least 3 characters';
        }

        if (!formData.shortCode.trim()) {
            newErrors.shortCode = 'Short code is required';
        } else if (formData.shortCode.trim().length < 2) {
            newErrors.shortCode = 'Short code must be at least 2 characters';
        } else if (!/^[A-Z0-9]+$/.test(formData.shortCode.trim())) {
            newErrors.shortCode = 'Short code must contain only uppercase letters and numbers';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        } else if (formData.description.trim().length < 10) {
            newErrors.description = 'Description must be at least 10 characters';
        }

        if (formData.category && formData.category.trim().length < 2) {
            newErrors.category = 'Category must be at least 2 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const response = await createService(formData);
            
            if (response.success) {
                onSuccess(response.data);
                handleClose();
            } else {
                setErrors({ submit: response.message || 'Failed to create service' });
            }
        } catch (error) {
            setErrors({ submit: 'An unexpected error occurred' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            name: '',
            shortCode: '',
            description: '',
            category: '',
            isMultiSession: false
        });
        setErrors({});
        setLoading(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">Add New Service</h2>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                    {/* Service Name */}
                    <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                            Service Name *
                        </label>
                        <Input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter service name"
                            error={errors.name}
                            required
                        />
                    </div>

                    {/* Short Code */}
                    <div>
                        <label htmlFor="shortCode" className="block text-sm font-medium text-gray-700 mb-1">
                            Short Code *
                        </label>
                        <Input
                            type="text"
                            id="shortCode"
                            name="shortCode"
                            value={formData.shortCode}
                            onChange={handleInputChange}
                            placeholder="e.g., HAIR_TR"
                            error={errors.shortCode}
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Use uppercase letters and numbers only
                        </p>
                    </div>

                    {/* Description */}
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            Description *
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Enter service description"
                            rows={3}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors.description ? 'border-red-500' : 'border-gray-300'
                            }`}
                            required
                        />
                        {errors.description && (
                            <p className="text-sm text-red-600 mt-1">{errors.description}</p>
                        )}
                    </div>

                    {/* Category */}
                    <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                            Category
                        </label>
                        <Input
                            type="text"
                            id="category"
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            placeholder="e.g., Hair Treatment, Skin Care"
                            error={errors.category}
                        />
                    </div>

                    {/* Multi-Session Toggle */}
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="isMultiSession"
                            name="isMultiSession"
                            checked={formData.isMultiSession}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isMultiSession" className="ml-2 block text-sm text-gray-900">
                            Multi-Session Service
                        </label>
                    </div>
                    <p className="text-xs text-gray-500">
                        Check if this service requires multiple sessions to complete
                    </p>

                    {/* Submit Error */}
                    {errors.submit && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                            <p className="text-sm text-red-600">{errors.submit}</p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex space-x-3 pt-4">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleClose}
                            className="flex-1"
                            loading={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            className="flex-1"
                            loading={loading}
                        >
                            Create Service
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddServiceModal; 